import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ProjectCostCodes = ({ projectVuid, onCostCodeChange }) => {
  const [costCodes, setCostCodes] = useState([]);
  const [projectCostCodes, setProjectCostCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCostCode, setEditingCostCode] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    status: 'active'
  });
  const [errors, setErrors] = useState({});
  
  // Integration state
  const [showRetrieveModal, setShowRetrieveModal] = useState(false);
  const [integrations, setIntegrations] = useState([]);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [retrievedCostCodes, setRetrievedCostCodes] = useState([]);
  const [showRetrievedCostCodesModal, setShowRetrievedCostCodesModal] = useState(false);
  const [selectedCostCodesToImport, setSelectedCostCodesToImport] = useState([]);
  const [retrievingCostCodes, setRetrievingCostCodes] = useState(false);
  const [importingCostCodes, setImportingCostCodes] = useState(false);

  useEffect(() => {
    if (projectVuid) {
      fetchCostCodes();
      fetchIntegrations();
    }
  }, [projectVuid]);
  
  const fetchIntegrations = async () => {
    try {
      const response = await axios.get('http://localhost:5001/api/integrations');
      if (response.data) {
        // Filter for Procore integrations that are active
        const procoreIntegrations = response.data.filter(integration => 
          integration.integration_type?.toLowerCase() === 'procore' && 
          integration.status === 'active'
        );
        setIntegrations(procoreIntegrations);
      }
    } catch (error) {
      console.error('Error fetching integrations:', error);
    }
  };

  const fetchCostCodes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`http://localhost:5001/api/projects/${projectVuid}/cost-codes`);
      setCostCodes(response.data);
      
      // Separate project-specific cost codes
      const projectSpecific = response.data.filter(cc => cc.is_project_specific);
      setProjectCostCodes(projectSpecific);
    } catch (error) {
      console.error('Error fetching cost codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.code.trim()) {
      newErrors.code = 'Cost code is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      const payload = {
        ...formData,
        project_vuid: projectVuid
      };
      
      if (editingCostCode) {
        await axios.put(`http://localhost:5001/api/project-cost-codes/${editingCostCode.vuid}`, payload);
      } else {
        await axios.post('http://localhost:5001/api/project-cost-codes', payload);
      }
      
      // Refresh cost codes
      await fetchCostCodes();
      
      // Reset form
      setFormData({
        code: '',
        description: '',
        status: 'active'
      });
      setShowCreateForm(false);
      setEditingCostCode(null);
      setErrors({});
      
      // Notify parent component
      if (onCostCodeChange) {
        onCostCodeChange();
      }
      
    } catch (error) {
      console.error('Error saving cost code:', error);
      if (error.response?.data?.error) {
        setErrors({ submit: error.response.data.error });
      } else {
        setErrors({ submit: 'Error saving cost code' });
      }
    }
  };

  const handleEdit = (costCode) => {
    setEditingCostCode(costCode);
    setFormData({
      code: costCode.code,
      description: costCode.description,
      status: costCode.status
    });
    setShowCreateForm(true);
  };

  const handleDelete = async (vuid) => {
    if (!window.confirm('Are you sure you want to delete this cost code?')) {
      return;
    }
    
    try {
      await axios.delete(`http://localhost:5001/api/project-cost-codes/${vuid}`);
      await fetchCostCodes();
      
      // Notify parent component
      if (onCostCodeChange) {
        onCostCodeChange();
      }
    } catch (error) {
      console.error('Error deleting cost code:', error);
      alert('Error deleting cost code');
    }
  };

  const handleCancel = () => {
    setFormData({
      code: '',
      description: '',
      status: 'active'
    });
    setShowCreateForm(false);
    setEditingCostCode(null);
    setErrors({});
  };
  
  const handleRetrieveCostCodes = () => {
    setSelectedIntegration(null);
    setShowRetrieveModal(true);
  };
  
  const fetchCostCodesFromIntegration = async (integrationVuid) => {
    if (!projectVuid) {
      alert('Project VUID is required');
      return;
    }
    
    try {
      setRetrievingCostCodes(true);
      const response = await axios.get(
        `http://localhost:5001/api/integrations/${integrationVuid}/retrieve-cost-codes`,
        { params: { project_vuid: projectVuid } }
      );
      
      if (response.data.success) {
        setRetrievedCostCodes(response.data.cost_codes || []);
        setSelectedCostCodesToImport([]);
        setShowRetrieveModal(false);
        setShowRetrievedCostCodesModal(true);
      } else {
        console.error('Error fetching cost codes:', response.data.error);
        alert(`Error: ${response.data.error || 'Failed to retrieve cost codes'}`);
        setRetrievedCostCodes([]);
      }
    } catch (error) {
      console.error('Error fetching cost codes from integration:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to retrieve cost codes';
      alert(`Error: ${errorMsg}`);
      setRetrievedCostCodes([]);
    } finally {
      setRetrievingCostCodes(false);
    }
  };
  
  const handleCostCodeSelection = (costCodeId) => {
    setSelectedCostCodesToImport(prev => {
      if (prev.includes(costCodeId)) {
        return prev.filter(id => id !== costCodeId);
      } else {
        return [...prev, costCodeId];
      }
    });
  };
  
  const handleSelectAllCostCodes = () => {
    if (selectedCostCodesToImport.length === retrievedCostCodes.length) {
      setSelectedCostCodesToImport([]);
    } else {
      setSelectedCostCodesToImport(retrievedCostCodes.map(cc => cc.id || cc.procore_id));
    }
  };
  
  const handleImportCostCodes = async () => {
    if (selectedCostCodesToImport.length === 0) {
      alert('Please select at least one cost code to import');
      return;
    }
    
    if (!window.confirm(`Import ${selectedCostCodesToImport.length} selected cost code(s)?`)) {
      return;
    }
    
    setImportingCostCodes(true);
    
    try {
      // Refresh cost codes list first to get the latest state
      await fetchCostCodes();
      
      const costCodesToImport = retrievedCostCodes.filter(cc => 
        selectedCostCodesToImport.includes(cc.id || cc.procore_id)
      );
      
      let importedCount = 0;
      let skippedCount = 0;
      let errorCount = 0;
      
      for (const costCode of costCodesToImport) {
        try {
          // Check if cost code already exists (using fresh data from fetchCostCodes)
          const existing = projectCostCodes.find(cc => cc.code === (costCode.code || costCode.full_code));
          if (existing) {
            console.log(`Cost code ${costCode.code || costCode.full_code} already exists, skipping`);
            skippedCount++;
            continue;
          }
          
          const payload = {
            code: costCode.code || costCode.full_code,
            description: costCode.description || costCode.name || `Imported from Procore: ${costCode.code || costCode.full_code}`,
            status: costCode.is_active !== false ? 'active' : 'inactive',
            project_vuid: projectVuid,
            procore_id: costCode.procore_id || costCode.id  // Include Procore ID for external ID mapping
          };
          
          const response = await axios.post('http://localhost:5001/api/project-cost-codes', payload);
          if (response.status === 201 || (response.status === 200 && response.data?.already_exists)) {
            // Count as imported if created (201) or already exists (200 with already_exists flag)
            if (response.status === 201) {
              importedCount++;
              // Add to local state immediately to prevent duplicate attempts
              if (response.data && response.data.vuid) {
                setProjectCostCodes(prev => [...prev, {
                  vuid: response.data.vuid,
                  code: response.data.code,
                  description: response.data.description,
                  status: response.data.status,
                  is_project_specific: true
                }]);
              }
            } else {
              // Already exists - don't count as error, but also don't increment importedCount
              // (since it wasn't actually imported in this operation)
              console.log(`Cost code ${costCode.code || costCode.full_code} already exists`);
            }
          } else {
            console.warn(`Unexpected status ${response.status} for cost code ${costCode.code || costCode.full_code}`);
            errorCount++;
          }
        } catch (error) {
          const errorMsg = error.response?.data?.error || error.message || 'Unknown error';
          // Don't count "already exists" as an error - it's expected
          if (errorMsg.toLowerCase().includes('already exists') || 
              errorMsg.toLowerCase().includes('duplicate') ||
              error.response?.status === 400) {
            console.log(`Cost code ${costCode.code || costCode.full_code} already exists: ${errorMsg}`);
            // Not counting as error since it's expected
          } else {
            console.error(`Error importing cost code ${costCode.code || costCode.full_code}:`, error);
            console.error(`  Error details: ${errorMsg}`);
            errorCount++;
          }
        }
      }
      
      // Refresh cost codes to show newly imported ones
      await fetchCostCodes();
      
      // Close modal and show success message
      setShowRetrievedCostCodesModal(false);
      setRetrievedCostCodes([]);
      setSelectedCostCodesToImport([]);
      
      // Show result message
      if (importedCount === 0 && errorCount > 0) {
        alert(`Failed to import any cost codes. ${errorCount} error(s) occurred. Please check the browser console for details.`);
      } else if (importedCount === 0 && skippedCount > 0) {
        alert(`All ${skippedCount} selected cost code(s) already exist. No new cost codes were imported.`);
      } else if (errorCount > 0) {
        alert(`Imported ${importedCount} cost code(s) successfully. ${skippedCount > 0 ? `${skippedCount} already existed. ` : ''}${errorCount} error(s) occurred. Please check the browser console for details.`);
      } else if (importedCount > 0) {
        const message = skippedCount > 0 
          ? `Successfully imported ${importedCount} cost code(s)! ${skippedCount} were already imported previously.`
          : `Successfully imported ${importedCount} cost code(s)!`;
        alert(message);
      } else {
        alert('No cost codes were imported. They may already exist.');
      }
      
      // Notify parent component
      if (onCostCodeChange) {
        onCostCodeChange();
      }
    } catch (error) {
      console.error('Error importing cost codes:', error);
      alert(`Error importing cost codes: ${error.message || 'Please check the browser console for details.'}`);
    } finally {
      setImportingCostCodes(false);
    }
  };

  if (loading) {
    return <div className="text-center py-4">Loading cost codes...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          Project Cost Codes
        </h3>
        <div className="flex space-x-3">
          <button
            onClick={handleRetrieveCostCodes}
            className="bg-gray-50 hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
            style={{ color: '#7F1D1D' }}
          >
            Retrieve Cost Codes
          </button>
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-gray-50 hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
            style={{ color: '#7F1D1D' }}
          >
            Add Cost Code
          </button>
        </div>
      </div>

      {/* Create Form (only show when creating, not editing) */}
      {showCreateForm && !editingCostCode && (
        <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <h4 className="text-md font-medium mb-4">
            Create New Cost Code
          </h4>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cost Code *
                </label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md ${
                    errors.code ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter cost code"
                />
                {errors.code && (
                  <p className="text-red-500 text-sm mt-1">{errors.code}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className={`w-full px-3 py-2 border rounded-md ${
                  errors.description ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter description"
              />
              {errors.description && (
                <p className="text-red-500 text-sm mt-1">{errors.description}</p>
              )}
            </div>
            
            {errors.submit && (
              <p className="text-red-500 text-sm">{errors.submit}</p>
            )}
            
            <div className="flex space-x-3">
              <button
                type="submit"
                className="bg-gray-50 hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                style={{ color: '#7F1D1D' }}
              >
                {editingCostCode ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="bg-gray-50 hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                style={{ color: '#7F1D1D' }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Project Cost Codes Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Cost Code
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {projectCostCodes.map((costCode) => (
              <React.Fragment key={costCode.vuid}>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {costCode.code}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {costCode.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      costCode.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {costCode.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(costCode)}
                      className="bg-gray-50 hover:bg-gray-100 font-semibold py-2 px-4 rounded-lg text-sm transition-all duration-200 transform hover:scale-105 shadow-lg mr-2"
                      style={{ color: '#7F1D1D' }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(costCode.vuid)}
                      className="bg-gray-50 hover:bg-gray-100 font-semibold py-2 px-4 rounded-lg text-sm transition-all duration-200 transform hover:scale-105 shadow-lg"
                      style={{ color: '#7F1D1D' }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
                {editingCostCode && editingCostCode.vuid === costCode.vuid && (
                  <tr>
                    <td colSpan="4" className="px-6 py-4 bg-gray-50">
                      <div className="p-4 border border-gray-200 rounded-lg bg-white">
                        <h4 className="text-md font-medium mb-4">Edit Cost Code</h4>
                        <form onSubmit={handleSubmit}>
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Cost Code *
                              </label>
                              <input
                                type="text"
                                name="code"
                                value={formData.code}
                                onChange={handleInputChange}
                                className={`w-full px-3 py-2 border rounded-md ${
                                  errors.code ? 'border-red-500' : 'border-gray-300'
                                }`}
                                placeholder="Enter cost code"
                              />
                              {errors.code && (
                                <p className="text-red-500 text-sm mt-1">{errors.code}</p>
                              )}
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Status
                              </label>
                              <select
                                name="status"
                                value={formData.status}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                              >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                              </select>
                            </div>
                          </div>
                          
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Description *
                            </label>
                            <textarea
                              name="description"
                              value={formData.description}
                              onChange={handleInputChange}
                              rows={3}
                              className={`w-full px-3 py-2 border rounded-md ${
                                errors.description ? 'border-red-500' : 'border-gray-300'
                              }`}
                              placeholder="Enter description"
                            />
                            {errors.description && (
                              <p className="text-red-500 text-sm mt-1">{errors.description}</p>
                            )}
                          </div>
                          
                          {errors.submit && (
                            <p className="text-red-500 text-sm mb-4">{errors.submit}</p>
                          )}
                          
                          <div className="flex space-x-3">
                            <button
                              type="submit"
                              className="bg-gray-50 hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                              style={{ color: '#7F1D1D' }}
                            >
                              Update
                            </button>
                            <button
                              type="button"
                              onClick={handleCancel}
                              className="bg-gray-50 hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                              style={{ color: '#7F1D1D' }}
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            
            {projectCostCodes.length === 0 && (
              <tr>
                <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                  No project-specific cost codes found. Create one to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Global Cost Codes Info */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="text-md font-medium text-blue-900 mb-2">
          Global Cost Codes
        </h4>
        <p className="text-sm text-blue-700">
          This project also has access to {costCodes.filter(cc => !cc.is_project_specific).length} global cost codes 
          from the master data. Project-specific cost codes are shown above and will appear in dropdowns 
          throughout this project.
        </p>
      </div>
      
      {/* Retrieve Cost Codes Modal */}
      {showRetrieveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Retrieve Cost Codes from Integration</h3>
            
            {integrations.length === 0 ? (
              <div className="mb-4">
                <p className="text-gray-600 mb-4">No Procore integrations available. Please configure an integration first.</p>
                <button
                  onClick={() => setShowRetrieveModal(false)}
                  className="w-full bg-gray-50 hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                  style={{ color: '#7F1D1D' }}
                >
                  Close
                </button>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Integration
                </label>
                <select
                  value={selectedIntegration?.vuid || ''}
                  onChange={(e) => {
                    const integration = integrations.find(i => i.vuid === e.target.value);
                    setSelectedIntegration(integration);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4"
                >
                  <option value="">-- Select Integration --</option>
                  {integrations.map(integration => (
                    <option key={integration.vuid} value={integration.vuid}>
                      {integration.integration_name}
                    </option>
                  ))}
                </select>
                
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      if (selectedIntegration) {
                        fetchCostCodesFromIntegration(selectedIntegration.vuid);
                      } else {
                        alert('Please select an integration');
                      }
                    }}
                    disabled={!selectedIntegration || retrievingCostCodes}
                    className="flex-1 bg-gray-50 hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg disabled:bg-gray-300 disabled:cursor-not-allowed disabled:transform-none"
                    style={{ color: '#7F1D1D' }}
                  >
                    {retrievingCostCodes ? 'Retrieving...' : 'Retrieve'}
                  </button>
                  <button
                    onClick={() => {
                      setShowRetrieveModal(false);
                      setSelectedIntegration(null);
                    }}
                    className="flex-1 bg-gray-50 hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                    style={{ color: '#7F1D1D' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Retrieved Cost Codes Modal */}
      {showRetrievedCostCodesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Select Cost Codes to Import</h3>
            
            {retrievedCostCodes.length === 0 ? (
              <div>
                <p className="text-gray-600 mb-4">No cost codes found.</p>
                <button
                  onClick={() => {
                    setShowRetrievedCostCodesModal(false);
                    setRetrievedCostCodes([]);
                  }}
                  className="bg-gray-50 hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                  style={{ color: '#7F1D1D' }}
                >
                  Close
                </button>
              </div>
            ) : (
              <div>
                <div className="mb-4 flex justify-between items-center">
                  <p className="text-sm text-gray-600">
                    Found {retrievedCostCodes.length} cost code(s). Select the ones you want to import.
                  </p>
                  <button
                    onClick={handleSelectAllCostCodes}
                    className="bg-gray-50 hover:bg-gray-100 font-semibold py-2 px-4 rounded-lg text-sm transition-all duration-200 transform hover:scale-105 shadow-lg"
                    style={{ color: '#7F1D1D' }}
                  >
                    {selectedCostCodesToImport.length === retrievedCostCodes.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                
                <div className="overflow-x-auto mb-4">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <input
                            type="checkbox"
                            checked={selectedCostCodesToImport.length === retrievedCostCodes.length && retrievedCostCodes.length > 0}
                            onChange={handleSelectAllCostCodes}
                            className="rounded border-gray-300 text-gray-800 focus:ring-gray-800"
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Code
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {retrievedCostCodes.map((costCode) => {
                        const isSelected = selectedCostCodesToImport.includes(costCode.id || costCode.procore_id);
                        const alreadyExists = projectCostCodes.some(cc => cc.code === (costCode.code || costCode.full_code));
                        
                        return (
                          <tr key={costCode.id || costCode.procore_id} className={alreadyExists ? 'bg-yellow-50' : ''}>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleCostCodeSelection(costCode.id || costCode.procore_id)}
                                disabled={alreadyExists}
                                className="rounded border-gray-300 text-gray-800 focus:ring-gray-800"
                              />
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                              {costCode.code || costCode.full_code}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {costCode.description || costCode.name || 'N/A'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {costCode.is_active !== false ? (
                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                  Active
                                </span>
                              ) : (
                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                  Inactive
                                </span>
                              )}
                              {alreadyExists && (
                                <span className="ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                  Already Exists
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                
                <div className="flex space-x-3">
                  <button
                    onClick={handleImportCostCodes}
                    disabled={selectedCostCodesToImport.length === 0 || importingCostCodes}
                    className="flex-1 bg-gray-50 hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg disabled:bg-gray-300 disabled:cursor-not-allowed disabled:transform-none"
                    style={{ color: '#7F1D1D' }}
                  >
                    {importingCostCodes ? `Importing... (${selectedCostCodesToImport.length})` : `Import Selected (${selectedCostCodesToImport.length})`}
                  </button>
                  <button
                    onClick={() => {
                      setShowRetrievedCostCodesModal(false);
                      setRetrievedCostCodes([]);
                      setSelectedCostCodesToImport([]);
                    }}
                    className="flex-1 bg-gray-50 hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                    style={{ color: '#7F1D1D' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectCostCodes;

